import React, { useState, useRef, useLayoutEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Pressable } from 'react-native';
import debounce from 'lodash.debounce';
import { Icons, PositionModal, Tooltip } from '@hmsexp/component-library';

const FilterSummary = ({ config, summary, selectedFilterKeys, setOpen }) => {
  const containerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(Object.keys(summary).length);
  const [showHidden, setShowHidden] = useState(false);
  const moreRef = useRef(null);
  const [parentPositions, setParentPositions] = useState(null);
  const handleMoreOpen = () => {
    setShowHidden(true);
    moreRef.current.measure((fx, fy, width, height, px, py) => {
      setParentPositions({ x: px, y: py + 3, w: width, h: height });
    });
  };
  const keys = Object.keys(summary);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const container = containerRef.current;
    if (!container) return;

    const recalcVisibleCount = debounce(() => {
      if (!container) return;
      const children = Array.from(container.children);
      const containerWidth = container.offsetWidth;
      const reservedWidth = containerWidth * 0.2;
      let totalWidth = 0;
      let newVisibleCount = keys.length;

      for (let i = 0; i < children.length; i++) {
        totalWidth += children[i].offsetWidth;
        if (totalWidth > containerWidth - reservedWidth) {
          newVisibleCount = i;
          break;
        }
      }

      setVisibleCount(newVisibleCount);
    }, 150);

    const resizeObserver = new ResizeObserver(recalcVisibleCount);
    resizeObserver.observe(container);

    recalcVisibleCount();

    const handleWindowResize = debounce(recalcVisibleCount, 150);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
      recalcVisibleCount.cancel();
      handleWindowResize.cancel();
    };
  }, [summary, keys.length]);

  const visibleKeys = keys.slice(0, visibleCount);
  const hiddenKeys = keys.slice(visibleCount);
  const hiddenCount = hiddenKeys.length;

  return (
    <Pressable
      style={styles.summaryRow}
      ref={containerRef}
      onPress={() => {
        setOpen(prev => !prev);
        window.scrollTo({
          top: 0,
          behavior: 'auto'
        });
      }}>
      {visibleKeys.length > 0 ? (
        visibleKeys.map(key => {
          const filter = config.find(f => f.key === key);
          if (!filter) return null;
          return (
            <Tooltip
              key={key}
              aretPosition='center'
              content={summary[key]}
              position='bottom'
              strictPosition
              style={{ container: { width: 250, zIndex: 100 } }}
              withCaret>
              <Pressable
                onPress={() => {
                  setOpen(prev => !prev);
                }}>
                <View style={styles.summaryItem}>
                  <Text style={styles.summaryLabel}>{filter.label}</Text>
                  <Text style={styles.summaryValue} numberOfLines={1}>
                    {summary[key]}
                  </Text>
                </View>
              </Pressable>
            </Tooltip>
          );
        })
      ) : (
        <Text style={styles.emptyText}>No filters applied</Text>
      )}

      {hiddenCount > 0 && (
        <TouchableOpacity onPress={() => handleMoreOpen()} ref={moreRef}>
          <Text style={styles.moreText}>+{hiddenCount} more</Text>
        </TouchableOpacity>
      )}

      <PositionModal visible={showHidden} outsideClick={() => setShowHidden(false)} parentPositions={parentPositions}>
        <View style={styles.hiddenContainer}>
          {hiddenKeys.map(key => {
            const filter = config.find(f => f.key === key);
            if (!filter) return null;
            return (
              <View key={key} style={styles.hiddenItem}>
                <Text style={styles.summaryLabel}>{filter.label}</Text>
                <Text style={styles.summaryValue}>{summary[key]}</Text>
              </View>
            );
          })}
        </View>
      </PositionModal>

      <TouchableOpacity style={styles.iconButton} onPress={() => setOpen(prev => !prev)}>
        <Icons.SlidersIcon size='L' color='#2d77d0' />
      </TouchableOpacity>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
    zIndex: 100,
    paddingVertical: 8,
    paddingHorizontal: '2%',
    flexWrap: 'nowrap',
    position: 'relative'
  },
  summaryItem: { marginRight: 20 },
  summaryLabel: { fontSize: 14, color: '#6b7280' },
  summaryValue: { fontSize: 16, color: '#111827', maxWidth: 160 },
  moreText: { color: '#2563eb', fontWeight: '500', marginRight: 12 },
  iconButton: { marginLeft: 'auto', transform: [{ rotate: '90deg' }] },
  emptyText: { color: '#9ca3af' },
  hiddenContainer: {
    backgroundColor: '#fff',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowRadius: 4,
    padding: 10,
    marginTop: 4,
    zIndex: 10,
    width: 200
  },
  hiddenItem: {
    marginBottom: 8
  }
});

export default FilterSummary;
